import { Radio, Tooltip, Typography } from "@mui/material";

/** COMPONENT */
import TableCustom from "../../../component/table/tableCustom";
import TableRowCommon from "../../../component/table/TableRowCommon";

/** UTILS */
import { StallReclaimLease } from "../../../api/stall-reclaim/stall-reclaim.api";
import { colors } from "../../../constants/colors";
import { leaseStatusColor } from "../../../constants/stallReclaim";
import { DASH, dateOnlyToView } from "../../depositRefund/depositRefund.utils";

type Props = {
  leases: StallReclaimLease[];
  /** leaseAgreementId ที่เลือกอยู่ — null = ยังไม่เลือก */
  selectedId: number | null;
  onSelect: (leaseAgreementId: number) => void;
  disabled?: boolean;
  textEmptyData?: string;
};

/** ความกว้างคอลัมน์ radio — TableRowCommon บังคับ width 160 ให้ cell แบบ COMPONENT ถ้าไม่ส่งมา */
const SELECT_COL_WIDTH = 56;

/**
 * ตาราง "แผงค้าภายใต้สัญญาเช่า" ใน modal สร้างคำสั่งเรียกคืน — เลือกได้ทีละสัญญา
 * (1 คำสั่ง = 1 สัญญา ตามกฎของ backend ที่ล็อกด้วย unique lock_key)
 *
 * ใช้ Radio ใน cell แบบ COMPONENT ไม่ใช้ option 'CHECKBOX' ของ TableRowCommon
 * เพราะตัวนั้นเป็น multi-select และ headCell ที่ label เป็น 'CHECKBOX' จะเข้าสาขา
 * dropdown เลือกทั้งหมดใน tableCustom.tsx แล้วพังที่ headCell.values.map
 *
 * ไม่ใช้ RadioGroup เพราะแถวถูกสร้างโดย TableRowCommon ครอบ tbody ไม่ได้ตามกฎ HTML
 * จึงคุม checked/onChange เองต่อแถว แต่ยังใส่ name เดียวกันทุกตัวเพื่อ a11y
 */
export default function ReclaimContractTable({
  leases,
  selectedId,
  onSelect,
  disabled,
  textEmptyData,
}: Props) {
  const headCells = [
    { id: "select", disablePadding: false, label: "", align: "center", width: SELECT_COL_WIDTH },
    { id: "leaseAgreementNo", disablePadding: false, label: "เลขที่สัญญา", align: "left" },
    { id: "zoneName", disablePadding: false, label: "โซน", align: "left" },
    { id: "stallName", disablePadding: false, label: "ชื่อแผงค้า", align: "left" },
    { id: "stallNumber", disablePadding: false, label: "เลขแผงค้า", align: "center" },
    { id: "dateEnd", disablePadding: false, label: "วันที่หมดสัญญา", align: "center" },
    { id: "leaseStatus", disablePadding: false, label: "สถานะสัญญา", align: "center" },
  ];

  const renderData = (objData: StallReclaimLease, no: number) => {
    const isSelected = objData.leaseAgreementId === selectedId;
    // can_reclaim = false -> มีคำสั่งค้างอยู่แล้ว เลือกไม่ได้ ต้องบอกเลขที่คำสั่งที่ยึดสิทธิไว้
    const rowDisabled = !!disabled || !objData.can_reclaim;

    const objRenderData = {
      key: `lease-${objData.leaseAgreementId}-${no}`,
      id: `lease-${objData.leaseAgreementId}`,
      obj: objData,
      // ได้ class MuiTableRow-root-active ไฮไลต์แถวที่เลือกฟรี ไม่ต้องเขียน style เพิ่ม
      rowSelect: isSelected,
      // กดที่แถวก็เลือกได้ ไม่ต้องเล็งปุ่ม radio (cell แบบ COMPONENT ไม่รับ onClick นี้)
      onClick: () => {
        if (!rowDisabled) onSelect(objData.leaseAgreementId);
      },
      columns: [
        {
          option: "COMPONENT",
          align: "center",
          width: SELECT_COL_WIDTH,
          // blocked_reason เป็นข้อความไทยพร้อมใช้จาก backend ตัวเดียวกับที่ POST ตอบกลับมา
          // Tooltip ต้องครอบด้วย span เพราะ MUI ไม่ยิง event ให้ element ที่ disabled
          component: (
            <Tooltip title={objData.can_reclaim ? "" : objData.blocked_reason || ""}>
              <span>
                <Radio
                  name="stall-reclaim-lease"
                  value={String(objData.leaseAgreementId)}
                  checked={isSelected}
                  disabled={rowDisabled}
                  onChange={() => onSelect(objData.leaseAgreementId)}
                  size="small"
                />
              </span>
            </Tooltip>
          ),
        },
        {
          option: "TEXT",
          align: "left",
          label: objData.lease_agreement_no || DASH,
          style: rowDisabled ? { color: colors.disabledGray } : undefined,
        },
        { option: "TEXT", align: "left", label: objData.zone_name || DASH },
        { option: "TEXT", align: "left", label: objData.stall_name || DASH },
        { option: "TEXT", align: "center", label: objData.stall_number || DASH },
        // DATE ที่ serialize เป็น UTC ISO ต้อง format ด้วย timezone Asia/Bangkok
        { option: "TEXT", align: "center", label: dateOnlyToView(objData.date_end) },
        {
          option: "COMPONENT",
          align: "center",
          component: (
            <Typography sx={{ fontSize: 16, color: leaseStatusColor(objData.lease_status) }}>
              {/* backend คืนคำไทยมาให้แล้ว ไม่ต้อง map เอง */}
              {objData.lease_status_name || objData.lease_status || DASH}
            </Typography>
          ),
        },
      ],
    };

    return <TableRowCommon {...objRenderData} />;
  };

  return (
    <TableCustom
      headCells={headCells}
      customScroll
      hidePagination
      page={1}
      // ห้ามเป็น 0 เด็ดขาด — Math.ceil(count / 0) = Infinity แล้ว renderPage วนไม่จบ แท็บค้าง
      pageLimit={leases.length || 1}
      sortType="ASC"
      sortBy=""
      rowCount={leases.length}
      rowsData={leases.map((lease, index) => renderData(lease, index))}
      onSort={() => {}}
      setPage={() => {}}
      setPageLimit={() => {}}
      textEmptyData={textEmptyData}
      tableMinWidth={900}
      // stickyHeader ต้องใช้คู่กับ tableMaxHeight ไม่งั้นไม่มีผล (MUI ต้องการ container สูงจำกัด)
      // และถ้าไม่ส่ง tableMaxHeight ตารางจะได้ minHeight calc(100% - 65px) ซึ่งระเบิดใน modal
      tableMaxHeight={320}
      stickyHeader
    />
  );
}
